import classNames from 'classnames';
import React from 'react';

import {AlertFlaggedRow} from './alert_types';
import {AlertRowBell} from './AlertRowBell';

/** The subset of material-table's `Column<T>` the bell column uses; structurally assignable to it in the
 * lists, without this package depending on material-table (only `layout` declares it). */
export type AlertRowBellColumn<T extends object> = {
  field: string;
  title: string;
  sorting: boolean;
  width: string;
  cellStyle: React.CSSProperties;
  headerStyle: React.CSSProperties;
  render: (data: T, type: 'row' | 'group') => React.ReactNode;
};

/** Row class of a list row that matches at least one alert; carries the red left-edge accent bar. */
export const ALERT_ROW_CLASS = 'dl-alert-row';
/** Width of the leading bell column, bell plus the cell's own left padding. A string on purpose:
 * MaterialTableWrapper's columnWidthPatch passes `width` straight to material-table's calc parsing,
 * which crashes on a number. */
const ALERT_COLUMN_WIDTH = '36px';

const ALERT_ROW_STYLE_ID = 'dl-alert-row-style';
/* The accent sits on the first cell rather than the row: tables collapse borders, where a box-shadow on
   a <tr> is not painted consistently. Injected once, like the light-cell stylesheet of MaterialTableWrapper,
   so every alert-eligible list gets it without owning a rule. */
function ensureAlertRowStyle() {
  if (typeof document === 'undefined' || document.getElementById(ALERT_ROW_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = ALERT_ROW_STYLE_ID;
  style.textContent = `tr.${ALERT_ROW_CLASS} > td:first-of-type{box-shadow:inset 4px 0 0 var(--dlColorWarning);}`;
  document.head.appendChild(style);
}

/**
 * The leading alert-bell column of an alert-eligible list (PDW-240): narrow, no header, not sortable, a
 * red bell with the matched-alerts tooltip on rows whose `alerts` is non-empty. Add it as the first column
 * ONLY when the user holds the Use alerts right - the AC removes the whole column otherwise, and the BE
 * sends an empty list for such users anyway, so an empty list can never be the signal.
 */
export function getAlertRowBellColumn<T extends AlertFlaggedRow>(): AlertRowBellColumn<T> {
  ensureAlertRowStyle();
  return {
    field: 'alerts',
    title: '',
    sorting: false,
    width: ALERT_COLUMN_WIDTH,
    cellStyle: {padding: '0 0 0 12px', width: ALERT_COLUMN_WIDTH, maxWidth: ALERT_COLUMN_WIDTH},
    headerStyle: {padding: 0, width: ALERT_COLUMN_WIDTH, maxWidth: ALERT_COLUMN_WIDTH},
    render: (data) => <AlertRowBell alerts={data.alerts ?? []} />,
  };
}

type GetRowClass<T> = (data: T, index: number, level: number) => string | undefined;

/** Striping as MaterialTableWrapper does it by default, kept when the alert class is layered on top. */
function defaultRowClass(index: number): string {
  return index % 2 === 0 ? 'ag-row-even' : 'ag-row-odd';
}

/**
 * A `getRowClass` for MaterialTableWrapper that adds [ALERT_ROW_CLASS] to rows with alerts, on top of the
 * wrapper's default striping or the list's own `getRowClass`.
 */
export function withAlertRowClass<T extends AlertFlaggedRow>(getRowClass?: GetRowClass<T>): GetRowClass<T> {
  return (data, index, level) => {
    const base = getRowClass ? getRowClass(data, index, level) : defaultRowClass(index);
    return classNames(base, data.alerts && data.alerts.length > 0 && ALERT_ROW_CLASS) || undefined;
  };
}
